import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Zap, Loader2, ShieldCheck } from "lucide-react";
import { PRODUCT, BUMP, PROMO_CODE, orderTotal } from "@/lib/data";
import { createOrder, createCheckout } from "@/lib/api";
import { track } from "@/lib/analytics";

const STEPS = ["Details", "Payment"];

export function CheckoutModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "" });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [showPromo, setShowPromo] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(() => localStorage.getItem("astra_promo") === PROMO_CODE);
  const [promoError, setPromoError] = useState("");
  const [bump, setBump] = useState(false);

  useEffect(() => {
    if (open && localStorage.getItem("astra_promo") === PROMO_CODE) setPromoApplied(true);
  }, [open]);

  const applyPromo = () => {
    if (promoInput.trim().toUpperCase() === PROMO_CODE) {
      setPromoApplied(true);
      setPromoError("");
      localStorage.setItem("astra_promo", PROMO_CODE);
    } else {
      setPromoError("That code isn't valid — check for typos.");
    }
  };

  const total = orderTotal(bump);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const close = () => {
    onClose();
    setTimeout(() => { setStep(0); setError(""); setProcessing(false); }, 300);
  };

  const pay = async () => {
    setProcessing(true);
    setError("");
    try {
      const order = await createOrder({ ...form, quantity: 1, bump });
      const { checkout_url } = await createCheckout(order.order_number, promoApplied);
      track("InitiateCheckout", { value: total, currency: "USD", order_id: order.order_number });
      window.location.href = checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start secure payment — please try again.");
      setProcessing(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-white/15 bg-[#0b0f18] px-4 py-3 text-base text-white placeholder-slate-600 outline-none transition-colors focus:border-cyan-400/60";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="checkout-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={close}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-white/15 bg-[#0e131f] p-7 sm:rounded-3xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">Secure Checkout</p>
                <h3 className="font-display mt-1 text-2xl font-bold text-white">{STEPS[step]}</h3>
              </div>
              <button data-testid="checkout-close-button" onClick={close} className="rounded-full border border-white/15 p-2 text-slate-400 transition-colors hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-5 flex gap-2">
              {STEPS.map((s, i) => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-cyan-400" : "bg-white/10"}`} />
              ))}
            </div>

            {step === 0 && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#131a29] p-4">
                  <img src={PRODUCT.image} alt="The 7-Night Sleep Reset cover" className="h-16 w-14 shrink-0 rounded-lg border border-white/10 object-cover" />
                  <div className="flex-1">
                    <p className="font-display font-bold text-white">{PRODUCT.name}</p>
                    <p className="text-xs text-slate-400">16-page guided PDF · instant download · 30-night guarantee</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-500 line-through">${PRODUCT.compareAt.toFixed(2)}</span>
                    <p className="font-display text-lg font-extrabold text-white">${PRODUCT.price}</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4 text-xs leading-relaxed text-slate-300">
                  <span className="font-bold text-cyan-300">Launch week:</span> 70% off the $67 program price.
                  Pay once, keep it forever — no subscription, ever.
                </div>

                <label
                  data-testid="bump-offer"
                  className={`flex cursor-pointer items-start gap-3.5 rounded-2xl border p-4 transition-colors ${bump ? "border-amber-400/50 bg-amber-400/10" : "border-amber-400/25 bg-amber-400/5 hover:border-amber-400/40"}`}
                >
                  <input
                    data-testid="bump-checkbox"
                    type="checkbox"
                    checked={bump}
                    onChange={(e) => setBump(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-amber-400"
                  />
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">One-time add-on — ${BUMP.price.toFixed(2)}</span>
                    <span className="font-display mt-1 block text-sm font-bold text-white">{BUMP.name}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-slate-400">{BUMP.tagline} Add it now and it downloads with your guide.</span>
                  </span>
                </label>

                {promoApplied ? (
                  <div data-testid="promo-applied-banner" className="flex items-center justify-between rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm">
                    <span className="font-semibold text-emerald-300">COMEBACK10 — extra 10% off applied at payment</span>
                    <button data-testid="promo-remove-button" onClick={() => { setPromoApplied(false); localStorage.removeItem("astra_promo"); }} className="text-xs text-slate-400 hover:text-white">Remove</button>
                  </div>
                ) : (
                  <div>
                    {!showPromo ? (
                      <button data-testid="promo-toggle-button" onClick={() => setShowPromo(true)} className="text-xs font-semibold text-slate-400 underline-offset-2 transition-colors hover:text-cyan-300 hover:underline">
                        Have a code?
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          data-testid="promo-code-input"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value)}
                          placeholder="Enter code"
                          className="flex-1 rounded-xl border border-white/15 bg-[#0b0f18] px-4 py-2.5 text-base uppercase tracking-wider text-white placeholder-slate-600 outline-none focus:border-cyan-400/60"
                        />
                        <button data-testid="promo-apply-button" onClick={applyPromo} className="rounded-xl border border-cyan-400/40 px-4 py-2.5 text-xs font-bold text-cyan-300 transition-colors hover:bg-cyan-400/10">
                          Apply
                        </button>
                      </div>
                    )}
                    {promoError && <p data-testid="promo-error" className="mt-1.5 text-xs text-rose-400">{promoError}</p>}
                  </div>
                )}

                <div className="flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="text-sm text-slate-400">Total</span>
                  <span className="font-display text-2xl font-extrabold text-white" data-testid="checkout-total">${total.toFixed(2)}</span>
                </div>
                <button data-testid="checkout-continue-button" onClick={() => setStep(1)} className="w-full rounded-full bg-cyan-400 py-4 text-base font-bold text-[#07090e] transition-transform hover:scale-[1.02] active:scale-95">
                  Continue
                </button>
              </div>
            )}

            {step === 1 && (
              <div className="mt-6 space-y-4">
                <input data-testid="checkout-name-input" className={inputCls} placeholder="First name" value={form.name} onChange={set("name")} />
                <div>
                  <input data-testid="checkout-email-input" className={inputCls} type="email" placeholder="Email address — your guide lands here" value={form.email} onChange={set("email")} />
                  <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                    <Zap className="h-3 w-3 text-cyan-400" /> Your download link is emailed here instantly after payment.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#131a29] p-4 text-sm text-slate-300">
                  <div className="flex justify-between"><span>{PRODUCT.name}</span><span>${PRODUCT.price.toFixed(2)}</span></div>
                  {bump && <div className="mt-1 flex justify-between"><span className="text-amber-300">+ {BUMP.name}</span><span>${BUMP.price.toFixed(2)}</span></div>}
                  <div className="mt-1 flex justify-between text-slate-500"><span>Delivery</span><span className="text-emerald-400">INSTANT</span></div>
                  {promoApplied && <div className="mt-1 flex justify-between"><span className="text-emerald-300">COMEBACK10</span><span className="text-emerald-300">−10% at payment</span></div>}
                  <div className="mt-3 flex justify-between border-t border-white/10 pt-3 font-bold text-white"><span>Total</span><span data-testid="payment-total">${total.toFixed(2)}</span></div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0b0f18] p-4 text-xs leading-relaxed text-slate-400">
                  <ShieldCheck className="h-8 w-8 shrink-0 text-emerald-400" />
                  <span>You'll be redirected to Stripe's secure checkout to pay by card, Apple Pay, or Google Pay. Your card details never touch our servers.</span>
                </div>
                {error && <p data-testid="checkout-error" className="text-sm text-rose-400">{error}</p>}
                <div className="flex gap-3">
                  <button data-testid="checkout-back-button" onClick={() => setStep(0)} className="rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-slate-300 transition-colors hover:text-white">Back</button>
                  <button
                    data-testid="checkout-submit-order-button"
                    onClick={pay}
                    disabled={processing || form.name.trim().length < 2 || !form.email.includes("@")}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-cyan-400 py-3.5 text-sm font-bold text-[#07090e] transition-all hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                    {processing ? "Redirecting to Stripe…" : `Pay $${total.toFixed(2)} — Get My Guide`}
                  </button>
                </div>
                <p className="text-center text-[11px] text-slate-500">Payments processed by Stripe · 30-night better-sleep guarantee</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
